import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  Download, TrendingUp, IndianRupee, Clock,
  Eye, AlertCircle, Receipt, RefreshCw,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, Button, Modal, StatCard, StatSkeleton, ActionBar } from '../ui';
import { PageHeader } from '../components/PageHeader';
import { formatCurrency } from '../utils';
import { fetchLawyerEarnings } from '../services/earningsService';

// ─── Status badge config ────────────────────────────────────────────────────────
const STATUS_STYLES = {
  pending:   { label: 'Pending',   cls: 'bg-amber-50 border border-amber-200 text-amber-700' },
  accepted:  { label: 'Accepted',  cls: 'bg-blue-50 border border-blue-200 text-blue-700' },
  completed: { label: 'Completed', cls: 'bg-emerald-50 border border-emerald-200 text-emerald-700' },
  rejected:  { label: 'Rejected',  cls: 'bg-red-50 border border-red-200 text-red-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-50 border border-slate-200 text-slate-500' },
};

function PaymentStatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

// ─── Skeleton row ───────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[...Array(6)].map((_, i) => (
        <td key={i} className="py-3 px-4">
          <div className="h-4 bg-slate-100 rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

export const PaymentsPage = ({ onNavigate }) => {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [selected, setSelected]   = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ── Fetch real data ────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchLawyerEarnings();
      setData(result);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    window.addEventListener('lawyer:booking-updated', load);
    return () => window.removeEventListener('lawyer:booking-updated', load);
  }, []);

  const transactions = data?.transactions || [];

  // ── Export to CSV ──────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const headers = [
      'Transaction ID', 'Client', 'Case Type',
      'Consultation Fee (₹)', 'Platform Fee (₹)', 'You Earn (₹)',
      'Date', 'Scheduled Date', 'Status',
    ];
    const rows = transactions.map((t) => [
      t.id,
      t.clientName,
      t.caseType,
      t.consultationFee,
      t.platformFee,
      t.lawyerEarnings,
      t.date ? format(new Date(t.date), 'dd MMM yyyy') : '',
      t.bookingDate ? `${t.bookingDate} ${t.bookingTime || ''}`.trim() : '',
      t.paymentStatus,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `earnings-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        title="Earnings"
        subtitle="Revenue overview and transaction history — real data from your bookings"
        showBack={false}
        rightAction={
          <ActionBar align="end">
            <Button variant="secondary" onClick={load} disabled={loading} style={{ minWidth: '44px', minHeight: '44px' }}>
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </Button>
            <Button variant="secondary" onClick={handleExportCSV} disabled={loading || !transactions.length} style={{ minHeight: '44px' }}>
              <Download size={16} className="mr-2" />
              Export CSV
            </Button>
          </ActionBar>
        }
      />

      <div>
        {/* Error banner */}
        {error && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle size={16} className="shrink-0" />
            {error}
            <button onClick={load} className="ml-auto font-semibold underline underline-offset-2">
              Retry
            </button>
          </div>
        )}

        {/* ── Summary cards ── */}
        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {loading ? (
            <>
              <StatSkeleton /><StatSkeleton /><StatSkeleton /><StatSkeleton />
            </>
          ) : (
            <>
              <StatCard
                label="Total Earnings"
                value={formatCurrency(data?.totalEarnings ?? 0)}
                icon={IndianRupee}
                color="emerald"
                sub={`After ${Math.round((data?.platformFeeRate ?? 0.1) * 100)}% platform fee`}
              />
              <StatCard
                label="This Month"
                value={formatCurrency(data?.monthlyEarnings ?? 0)}
                icon={TrendingUp}
                color="blue"
                sub="Completed this month"
              />
              <StatCard
                label="Upcoming Amount"
                value={formatCurrency(data?.upcomingEarnings ?? 0)}
                icon={Clock}
                color="blue"
                sub="Accepted, not completed"
              />
              <StatCard
                label="Pending Amount"
                value={formatCurrency(data?.pendingEarnings ?? 0)}
                icon={Clock}
                color="amber"
                sub="Awaiting lawyer acceptance"
              />
            </>
          )}
        </div>

        {/* ── Transaction table ── */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>All Transactions</CardTitle>
            {!loading && (
              <span className="text-xs text-slate-500 font-normal">
                {transactions.length} record{transactions.length !== 1 ? 's' : ''}
              </span>
            )}
          </CardHeader>

          {/* Mobile Card View (< lg) */}
          <div className="lg:hidden p-3 sm:p-4 space-y-3">
            {loading ? (
              <>
                <div className="h-28 bg-slate-100 rounded-xl animate-pulse" />
                <div className="h-28 bg-slate-100 rounded-xl animate-pulse" />
                <div className="h-28 bg-slate-100 rounded-xl animate-pulse" />
              </>
            ) : transactions.length === 0 ? (
              <div className="py-12 text-center text-slate-400 flex flex-col items-center gap-2">
                <Receipt size={32} strokeWidth={1.5} />
                <p className="text-sm font-medium">No transactions yet</p>
              </div>
            ) : (
              transactions.map((t) => (
                <div key={t.id} className="border border-slate-200 rounded-xl p-3.5 bg-slate-50/50 space-y-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-bold text-slate-900 text-sm sm:text-base">{t.clientName}</p>
                      <p className="text-slate-500 text-xs mt-0.5">{t.caseType} • {t.date ? format(new Date(t.date), 'dd MMM yyyy') : '—'}</p>
                    </div>
                    <PaymentStatusBadge status={t.paymentStatus} />
                  </div>

                  <div className="flex items-center justify-between text-xs sm:text-sm pt-2 border-t border-slate-200/60">
                    <div>
                      <span className="text-slate-500">You Earn: </span>
                      <span className="font-extrabold text-emerald-700 text-sm sm:text-base">{formatCurrency(t.lawyerEarnings)}</span>
                      <span className="text-[11px] text-slate-400 block sm:inline sm:ml-1"> (Gross {formatCurrency(t.consultationFee)} - Fee {formatCurrency(t.platformFee)})</span>
                    </div>
                    <button
                      style={{ minWidth: '44px', minHeight: '44px' }}
                      onClick={() => { setSelected(t); setModalOpen(true); }}
                      className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 font-semibold text-xs flex items-center justify-center gap-1 shrink-0 text-slate-700 transition-colors"
                    >
                      <Eye size={14} /> View
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table View (>= lg) */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-y border-slate-100">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Client</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Case Type</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-600">Gross Fee</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-600">Platform Fee</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-600">You Earn</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Date</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Status</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-600">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <>
                    <SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow />
                  </>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3 text-slate-400">
                        <Receipt size={36} strokeWidth={1.5} />
                        <p className="text-sm font-medium">No transactions yet</p>
                        <p className="text-xs">Transactions will appear here once consultations are completed</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  transactions.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <p className="font-semibold text-slate-800">{t.clientName}</p>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-xs">{t.caseType}</td>
                      <td className="py-3 px-4 text-right text-slate-700 font-medium">
                        {formatCurrency(t.consultationFee)}
                      </td>
                      <td className="py-3 px-4 text-right text-red-500 text-xs">
                        −{formatCurrency(t.platformFee)}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-emerald-700">
                        {formatCurrency(t.lawyerEarnings)}
                      </td>
                      <td className="py-3 px-4 text-center text-slate-500 text-xs whitespace-nowrap">
                        {t.date ? format(new Date(t.date), 'dd MMM yyyy') : '—'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <PaymentStatusBadge status={t.paymentStatus} />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setSelected(t); setModalOpen(true); }}
                        >
                          <Eye size={15} />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* ── Detail modal ── */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Transaction Details">
        {selected && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-sm text-slate-500">Booking for {selected.clientName}</p>
              <p className="text-4xl font-bold text-slate-900 mt-1">
                {formatCurrency(selected.lawyerEarnings)}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                After {formatCurrency(selected.platformFee)} platform fee
              </p>
              <div className="mt-2">
                <PaymentStatusBadge status={selected.paymentStatus} />
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Transaction ID</span>
                <span className="font-mono text-xs text-slate-700 break-all text-right max-w-[60%]">{selected.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Client Name</span>
                <span className="font-semibold text-slate-800">{selected.clientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Case Type</span>
                <span className="text-slate-800">{selected.caseType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Consultation Fee</span>
                <span className="font-semibold text-slate-800">{formatCurrency(selected.consultationFee)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Platform Fee (10%)</span>
                <span className="text-red-500">−{formatCurrency(selected.platformFee)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-3 mt-1">
                <span className="font-semibold text-slate-700">You Receive</span>
                <span className="font-bold text-emerald-700 text-base">{formatCurrency(selected.lawyerEarnings)}</span>
              </div>
              {selected.bookingDate && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Consultation Slot</span>
                  <span className="text-slate-800">
                    {selected.bookingDate} {selected.bookingTime ? `at ${selected.bookingTime}` : ''}
                  </span>
                </div>
              )}
              {selected.date && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Booking Date</span>
                  <span className="text-slate-800">{format(new Date(selected.date), 'dd MMM yyyy, HH:mm')}</span>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => setModalOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
